import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import { cn } from "@nt/ui/cn";
import { focusRing } from "@nt/ui/recipes";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/**
 * A pill track of two to five mutually exclusive options (Yes / No, a range,
 * Day / Month): the PillTabs look on Base UI's ToggleGroup. Exactly one is
 * always on — pressing the current option again keeps it.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = "default",
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: {
  value: T | null;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  size?: "sm" | "default";
  disabled?: boolean;
  className?: string;
  "aria-label": string;
}) {
  return (
    <ToggleGroup
      data-slot="segmented"
      aria-label={ariaLabel}
      value={value === null ? [] : [value]}
      disabled={disabled}
      onValueChange={(next) => {
        const picked = next[0] as T | undefined;
        if (picked !== undefined && picked !== value) onChange(picked);
      }}
      className={cn(
        "inline-flex w-fit max-w-full items-center gap-0.5 overflow-x-auto rounded-pill bg-muted p-1 [scrollbar-width:none]",
        size === "sm" ? "h-8" : "h-control",
        className,
      )}
    >
      {options.map((o) => (
        <Toggle
          key={o.value}
          value={o.value}
          data-slot="segmented-item"
          className={cn(
            "inline-flex h-full shrink-0 items-center justify-center rounded-pill font-medium whitespace-nowrap text-muted-foreground transition-[color,background-color,box-shadow] duration-150 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 data-pressed:bg-card data-pressed:text-foreground data-pressed:shadow-chip",
            size === "sm" ? "px-2.5 text-xs" : "px-3.5 text-sm",
            focusRing,
          )}
        >
          {o.label}
        </Toggle>
      ))}
    </ToggleGroup>
  );
}
